/**
 * Point-of-decision cue for a two-part-kit COMPONENT in a product picker.
 *
 * A CSR choosing between `F3105.03SC1` (the GreenThane Pt. A pail) and
 * `F3105.04SC1` (the KIT) sees only `price / UOM` in the search results, so
 * the component is an easy wrong pick — AU01042 and AU02050 both went out
 * on the component code. The search API now marks components
 * (`kit_component_part` + `kit_parent_code`, NOT gated on price); this badge
 * renders that mark wherever a result row is drawn. Null when the result is
 * not a component.
 */
import type { ProductSearchResult } from '../../services/portalProducts';

export interface KitComponentBadgeProps {
  product: Pick<ProductSearchResult, 'sage_item_code' | 'kit_component_part' | 'kit_parent_code'>;
}

export function kitComponentTitle(
  product: KitComponentBadgeProps['product'],
): string | null {
  if (!product.kit_component_part) return null;
  const parent = product.kit_parent_code;
  return parent
    ? `${product.sage_item_code} is Part ${product.kit_component_part} of two-part kit ${parent}. ` +
        `Customers ordering the product by name buy the kit ${parent} (UOM=KIT); ` +
        'the component alone ships an incomplete product.'
    : `${product.sage_item_code} is Part ${product.kit_component_part} of a two-part kit — ` +
        'check the family for the kit-parent SKU before using this code.';
}

export default function KitComponentBadge({ product }: KitComponentBadgeProps) {
  const title = kitComponentTitle(product);
  if (!title) return null;
  const parent = product.kit_parent_code;
  return (
    <span
      role="note"
      data-testid="kit-component-badge"
      title={title}
      style={{
        fontSize: '10px',
        fontWeight: 700,
        color: '#b45309',
        background: 'rgba(251, 191, 36, 0.18)',
        border: '1px solid rgba(217, 119, 6, 0.35)',
        borderRadius: '3px',
        padding: '1px 5px',
        marginLeft: '6px',
        whiteSpace: 'nowrap',
      }}
    >
      ⚠ Part {product.kit_component_part}
      {parent ? ` of kit ${parent}` : ' (kit component)'}
    </span>
  );
}
